import { Link } from "react-router-dom";
import { useState } from "react";
import { motion } from "framer-motion";
import {
  Megaphone,
  Users,
  FlaskConical,
  TrendingUp,
  ArrowRight,
  Check,
  Mail,
  LucideIcon,
} from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import RequestQuoteDialog from "@/components/affiliates/RequestQuoteDialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

const EMRKT_AFFILIATE_ID = "6ce2f5fc-4791-4f80-8c70-a756f6b60751";

interface Service {
  num: string;
  title: string;
  icon: LucideIcon;
  tagline: string;
  description: string;
  deliverables: string[];
  proofHref: string;
  proofLabel: string;
}

const SERVICES: Service[] = [
  {
    num: "01",
    title: "Advocacy",
    icon: Users,
    tagline: "Coalitions and grassroots pressure that turn voice into policy.",
    description:
      "We organize the people, partners, and institutions whose support makes a position credible. We build coalitions across Alabama, run grasstops outreach, and convert pressure into commitments from decision-makers.",
    deliverables: [
      "Coalition design and stakeholder mapping",
      "Grassroots and grasstops mobilization",
      "Public testimony, sign-on letters, and rapid response",
      "Partner network activation across our directory",
    ],
    proofHref: "/partners/directory",
    proofLabel: "See our partner network",
  },
  {
    num: "02",
    title: "Strategic Communication",
    icon: Megaphone,
    tagline: "Narrative discipline that holds up in the room and online.",
    description:
      "We sharpen the story, train the messengers, and place it where it matters. From earned media to social signal monitoring, we keep your message coherent across every channel that shapes the decision.",
    deliverables: [
      "Message frameworks and spokesperson prep",
      "Earned media strategy and press outreach",
      "Digital signal monitoring and content production",
      "Crisis communication and rapid response",
    ],
    proofHref: "/insights",
    proofLabel: "See how we publish",
  },
  {
    num: "03",
    title: "Research",
    icon: FlaskConical,
    tagline: "Evidence that holds up when the conversation gets serious.",
    description:
      "We produce the data, briefings, and white papers that anchor policy and funding arguments. Our research practice pairs Alabama-specific datasets with applied analysis so clients walk in prepared.",
    deliverables: [
      "County-level data analysis across all 67 AL counties",
      "Issue briefings and policy memos",
      "White papers and longform reports",
      "Custom dashboards for ongoing intelligence",
    ],
    proofHref: "/data-tools",
    proofLabel: "Explore our data tools",
  },
  {
    num: "04",
    title: "Economic Development",
    icon: TrendingUp,
    tagline: "Capital strategy that moves dollars into community-rooted work.",
    description:
      "We help clients find, qualify for, and win the grants, contracts, and capital programs designed for the work they already do. We sit between the funder, the program, and the organization until the dollars move.",
    deliverables: [
      "Grant strategy and pipeline development",
      "Procurement and contract readiness (incl. DBE)",
      "Capital stack design for community projects",
      "Funder and agency engagement",
    ],
    proofHref: "/opportunities",
    proofLabel: "See the funding feed",
  },
];

export default function Services() {
  const [quoteOpen, setQuoteOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main>
        {/* Hero */}
        <section className="relative border-b border-border">
          <div className="container mx-auto px-6 py-16 md:py-24 max-w-5xl">
            <span className="text-primary text-xs font-semibold tracking-[0.2em] uppercase">
              Our Services
            </span>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mt-3 mb-5">
              Hire the firm <span className="text-gradient-primary">behind the platform.</span>
            </h1>
            <p className="text-muted-foreground text-lg md:text-xl leading-relaxed max-w-3xl">
              The Emerging Market is an Alabama government affairs firm built for small,
              community-rooted organizations that rarely have a partner in the room. We work
              across four practices — and we put the same live tools we use into our clients' hands.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Button size="lg" onClick={() => setQuoteOpen(true)}>
                <Mail className="h-4 w-4 mr-2" />
                Engage Our Firm
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/affiliates/the-emerging-market">
                  See our partner profile
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Practices */}
        <section className="py-16 md:py-24 border-b border-border">
          <div className="container mx-auto px-6 max-w-5xl">
            <div className="grid gap-5 md:gap-6">
              {SERVICES.map((s, i) => (
                <motion.div
                  key={s.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                >
                  <Card className="p-6 md:p-8 grid md:grid-cols-[1fr_1.4fr] gap-6 md:gap-10 bg-card/60 hover:bg-card transition-colors">
                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center">
                          <s.icon className="h-5 w-5 text-primary" />
                        </div>
                        <span className="font-display text-xs font-semibold text-muted-foreground tracking-[0.2em]">
                          {s.num}
                        </span>
                      </div>
                      <h2 className="font-display text-2xl md:text-3xl font-bold mb-2">{s.title}</h2>
                      <p className="text-primary/90 text-sm md:text-base leading-relaxed mb-4">
                        {s.tagline}
                      </p>
                      <Link
                        to={s.proofHref}
                        className="inline-flex items-center gap-1 text-xs text-primary font-semibold hover:underline"
                      >
                        {s.proofLabel}
                        <ArrowRight className="h-3 w-3" />
                      </Link>
                    </div>
                    <div>
                      <p className="text-muted-foreground leading-relaxed mb-5">{s.description}</p>
                      <ul className="space-y-2">
                        {s.deliverables.map((d) => (
                          <li key={d} className="flex items-start gap-2 text-sm">
                            <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                            <span>{d}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* How we engage */}
        <section className="py-16 md:py-20 border-b border-border bg-card/30">
          <div className="container mx-auto px-6 max-w-5xl">
            <span className="text-primary text-xs font-semibold tracking-[0.2em] uppercase">
              How we engage
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-bold mt-3 mb-8">
              Three ways to bring us on
            </h2>
            <div className="grid md:grid-cols-3 gap-4">
              {[
                {
                  title: "Retained representation",
                  body: "Ongoing government affairs counsel — bills, coalitions, communications, and capital — across a legislative session or program year.",
                },
                {
                  title: "Project engagement",
                  body: "Scoped work for a named outcome: a campaign, a grant pipeline, a policy memo, or a stakeholder engagement plan.",
                },
                {
                  title: "Advisory & research",
                  body: "Standalone research, briefings, or strategic advisory for funders, agencies, and anchor institutions.",
                },
              ].map((o) => (
                <Card key={o.title} className="p-6 bg-background">
                  <h3 className="font-display font-bold text-lg mb-2">{o.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{o.body}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-6 max-w-4xl text-center">
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">
              Ready to get the firm in the room?
            </h2>
            <p className="text-muted-foreground text-lg leading-relaxed mb-8 max-w-2xl mx-auto">
              Tell us what you're working on. We'll come back with a read on the landscape and a
              proposed engagement.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Button size="lg" onClick={() => setQuoteOpen(true)}>
                <Mail className="h-4 w-4 mr-2" />
                Engage Our Firm
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/affiliates/the-emerging-market">View partner profile</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <RequestQuoteDialog
        open={quoteOpen}
        onOpenChange={setQuoteOpen}
        affiliateId={EMRKT_AFFILIATE_ID}
        affiliateName="The Emerging Market"
      />
    </div>
  );
}